import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'
import { useCustomerStore } from './customer'
import { doc, onSnapshot } from 'firebase/firestore'
import { firestore } from '@/firebase'
import { subscriptionSchema, type StripeProductId, type Subscription } from '@gh/common/models'
import {
  // isSubscriptionActive as isSubscriptionActiveUtils,
  isPaymentActive as isPaymentActiveUtils,
} from '@/components/subscriptions/utils'
import createLogger from '@/utils/logger'

const logger = createLogger('stores/subscription.ts')

export const useSubscriptionStore = defineStore('subscription', () => {
  const customerStore = useCustomerStore()
  const customer = computed(() => customerStore.customer)
  const subscription = ref<Subscription | null>(null)
  const isLoading = ref(false)
  let unsubscribeSubscriptionSnapshot: (() => void) | null = null

  watch(
    customer,
    () => {
      retrieveSubscriptionFromFirestore()
    },
    { immediate: true },
  )

  function retrieveSubscriptionFromFirestore() {
    if (unsubscribeSubscriptionSnapshot) {
      unsubscribeSubscriptionSnapshot()
    }

    const id = customer.value?.customerId
    if (!id) {
      subscription.value = null
      isLoading.value = false
      return
    }

    isLoading.value = true
    unsubscribeSubscriptionSnapshot = onSnapshot(
      doc(firestore, 'subscriptions', id),
      (docSnap) => {
        try {
          if (docSnap.exists()) {
            const { success, data, error } = subscriptionSchema.safeParse({
              id: docSnap.id,
              ...docSnap.data(),
            })
            if (!success) {
              logger.error('Error parsing subscription data', { data, error, snap: docSnap.data() })
              return
            }
            subscription.value = data
          } else {
            subscription.value = null
          }
        } finally {
          isLoading.value = false
        }
      },
      (error) => {
        logger.error('Error fetching subscription snapshot', error)
        isLoading.value = false
      },
    )
  }

  // function isSubscriptionActive() {
  //   return isSubscriptionActiveUtils(customer.value, subscription.value)
  // }

  function isPaymentActive() {
    return isPaymentActiveUtils(customer.value, subscription.value)
  }

  function getCurrentStripeProductId(): StripeProductId | null {
    return (
      subscription.value?.subscriptionInfo?.stripeProductId ||
      customer.value?.signupPaymentInfo?.stripeProductId ||
      null
    )
  }

  const hasSubscription = computed(() => !!subscription.value?.subscriptionInfo)

  const isSubscriptionPastDue = computed(
    () => subscription.value?.subscriptionInfo?.stripeStatus === 'past_due',
  )

  function signOut() {
    if (unsubscribeSubscriptionSnapshot) {
      unsubscribeSubscriptionSnapshot()
    }
    subscription.value = null
    isLoading.value = false
  }

  return {
    subscription,
    isLoading,
    // isSubscriptionActive,
    isPaymentActive,
    getCurrentStripeProductId,
    hasSubscription,
    isSubscriptionPastDue,
    signOut,
  }
})
